import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { api } from "../api";

function clean(value) {
  return value == null
    ? ""
    : String(value).trim();
}

function humanType(value) {
  return clean(value)
    .replace(/_/g, " ")
    .replace(
      /\b\w/g,
      (letter) =>
        letter.toUpperCase()
    );
}

function sceneHeading(scene) {
  return clean(
    (
      Array.isArray(
        scene?.elements
      )
        ? scene.elements
        : []
    ).find(
      (element) =>
        clean(element?.type) ===
        "scene_heading"
    )?.text
  );
}

export default function ScreenplayReviewDecideWorkspace({
  projectId,
  access = null,
  active = false,
  isFR = false,
  onOpenProposal,
}) {
  const isOwner =
    access?.relationship ===
      "owner" ||
    access?.canManageProject ===
      true;

  const [loading, setLoading] =
    useState(false);
  const [
    workingId,
    setWorkingId,
  ] = useState("");
  const [error, setError] =
    useState("");
  const [notice, setNotice] =
    useState("");
  const [
    proposalRound,
    setProposalRound,
  ] = useState(null);
  const [
    contributions,
    setContributions,
  ] = useState([]);
  const [context, setContext] =
    useState(null);
  const [notes, setNotes] =
    useState({});
  const [
    rejectAllNote,
    setRejectAllNote,
  ] = useState("");

  const loadReview =
    useCallback(
      async () => {
        const id =
          clean(projectId);

        if (
          !active ||
          !id ||
          !isOwner
        ) {
          setLoading(false);
          setProposalRound(null);
          setContributions([]);
          setContext(null);
          setError("");
          return;
        }

        try {
          setLoading(true);
          setError("");

          const current =
            await api
              .getCurrentScreenplayProposalRound(
                id
              );

          const round =
            current?.proposalRound ||
            null;

          setProposalRound(round);

          if (!round) {
            setContributions([]);
            setContext(null);
            return;
          }

          const [
            inventory,
            contextPayload,
          ] =
            await Promise.all([
              api
                .getScreenplayProposalRoundContributions(
                  id,
                  round.id
                ),
              api
                .getScreenplayProposalRoundContext(
                  id,
                  round.id
                ),
            ]);

          setContributions(
            Array.isArray(
              inventory
                ?.contributions
            )
              ? inventory
                  .contributions
              : []
          );

          setContext(
            contextPayload
              ?.context ||
            null
          );
        } catch (err) {
          setError(
            clean(err?.message) ||
            (
              isFR
                ? "Impossible de charger les contributions."
                : "Could not load the contributions."
            )
          );
        } finally {
          setLoading(false);
        }
      },
      [
        active,
        isFR,
        isOwner,
        projectId,
      ]
    );

  useEffect(() => {
    loadReview();
  }, [loadReview]);

  const pending =
    useMemo(
      () =>
        contributions.filter(
          (contribution) =>
            clean(
              contribution
                ?.decisionStatus
            ) === "pending"
        ),
      [contributions]
    );

  function targetLabel(
    contribution
  ) {
    const sceneId =
      clean(
        contribution
          ?.scope
          ?.contentUnitId
      );

    const elementId =
      clean(
        contribution
          ?.scope
          ?.subUnitId
      );

    const scene =
      (
        Array.isArray(
          context?.scenes
        )
          ? context.scenes
          : []
      ).find(
        (row) =>
          clean(
            row?.sceneId
          ) === sceneId
      ) || null;

    const sceneName =
      scene
        ? `Scene ${
            Number(
              scene?.order
            ) || ""
          }${
            sceneHeading(
              scene
            )
              ? `: ${sceneHeading(
                  scene
                )}`
              : ""
          }`
        : (
            isFR
              ? "Scène ciblée"
              : "Target scene"
          );

    if (!elementId) {
      return sceneName;
    }

    const element =
      (
        Array.isArray(
          scene?.elements
        )
          ? scene.elements
          : []
      ).find(
        (row) =>
          clean(
            row?.elementId
          ) === elementId
      ) || null;

    return element
      ? `${sceneName} · ${humanType(
          element?.type
        )}`
      : sceneName;
  }

  function proposalBody(
    contribution
  ) {
    const snapshot =
      contribution
        ?.contentSnapshot;

    if (
      contribution
        ?.contributionType ===
      "scene_alternative"
    ) {
      return (
        Array.isArray(
          snapshot?.elements
        )
          ? snapshot.elements
          : []
      ).map(
        (
          element,
          index
        ) => (
          <div
            key={
              element
                ?.elementId ||
              index
            }
            style={{
              marginTop:
                index === 0
                  ? 0
                  : 10,
            }}
          >
            <strong
              style={{
                fontSize: 12,
                textTransform:
                  "uppercase",
              }}
            >
              {humanType(
                element?.type
              )}
            </strong>

            <div
              style={{
                marginTop: 3,
                whiteSpace:
                  "pre-wrap",
                lineHeight: 1.5,
              }}
            >
              {String(
                element?.text ??
                ""
              )}
            </div>
          </div>
        )
      );
    }

    return (
      <div>
        <strong
          style={{
            fontSize: 12,
            textTransform:
              "uppercase",
          }}
        >
          {humanType(
            snapshot?.type
          )}
        </strong>

        <div
          style={{
            marginTop: 3,
            whiteSpace:
              "pre-wrap",
            lineHeight: 1.5,
          }}
        >
          {String(
            snapshot?.text ??
            ""
          )}
        </div>
      </div>
    );
  }

  function setDecisionNote(
    contributionId,
    value
  ) {
    setNotes(
      (current) => ({
        ...current,
        [contributionId]:
          value,
      })
    );
  }

  async function decide(
    contribution,
    action
  ) {
    const contributionId =
      clean(
        contribution?.id
      );

    const note =
      clean(
        notes[
          contributionId
        ]
      );

    if (
      !contributionId ||
      !note ||
      !proposalRound
    ) {
      setError(
        isFR
          ? "Ajoutez une note de décision avant de continuer."
          : "Add a decision note before continuing."
      );
      return;
    }

    try {
      setWorkingId(
        contributionId
      );
      setError("");
      setNotice("");

      await api
        .decideScreenplayContribution(
          projectId,
          proposalRound.id,
          contributionId,
          {
            action,
            expectedRoundRevision:
              proposalRound
                .revision,
            expectedDraftRevision:
              Number(
                proposalRound
                  ?.scope
                  ?.baseContentRevision
              ),
            decisionNote:
              note,
          }
        );

      setNotes(
        (current) => {
          const next = {
            ...current,
          };
          delete next[
            contributionId
          ];
          return next;
        }
      );

      setNotice(
        action === "accept"
          ? (
              isFR
                ? "Contribution acceptée et appliquée au scénario."
                : "Contribution accepted and applied to the screenplay."
            )
          : (
              isFR
                ? "Contribution rejetée."
                : "Contribution rejected."
            )
      );

      await loadReview();
    } catch (err) {
      setError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible d’enregistrer cette décision."
            : "Could not record this decision."
        )
      );
    } finally {
      setWorkingId("");
    }
  }

  async function rejectAll(event) {
    event?.preventDefault?.();

    const note =
      clean(rejectAllNote);

    if (
      !proposalRound ||
      !note
    ) {
      setError(
        isFR
          ? "Ajoutez une note de décision avant de rejeter toutes les contributions."
          : "Add a decision note before rejecting all contributions."
      );
      return;
    }

    try {
      setWorkingId(
        "reject-all"
      );
      setError("");
      setNotice("");

      await api
        .rejectAllScreenplayContributions(
          projectId,
          proposalRound.id,
          {
            action:
              "reject_all",
            expectedRoundRevision:
              proposalRound
                .revision,
            expectedDraftRevision:
              Number(
                proposalRound
                  ?.scope
                  ?.baseContentRevision
              ),
            decisionNote:
              note,
          }
        );

      setRejectAllNote("");
      setNotice(
        isFR
          ? "Toutes les contributions en attente ont été rejetées et le Proposal Round est clôturé."
          : "All pending contributions were rejected and the Proposal Round was closed."
      );

      await loadReview();
    } catch (err) {
      setError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de rejeter toutes les contributions."
            : "Could not reject all contributions."
        )
      );
    } finally {
      setWorkingId("");
    }
  }

  if (!isOwner) {
    return (
      <section
        className="ns-card"
        style={{
          padding: 20,
        }}
      >
        <h3
          className="ns-card-title"
          style={{
            marginTop: 0,
          }}
        >
          Review and Decide
        </h3>

        <p
          className="ns-card-meta"
          style={{
            marginBottom: 0,
          }}
        >
          {isFR
            ? "La décision finale sur les contributions appartient au propriétaire du projet."
            : "Final contribution decisions belong to the project Owner."}
        </p>
      </section>
    );
  }

  return (
    <section
      className="ns-card"
      aria-label="Screenplay Review and Decide"
      style={{
        padding: 20,
        borderRadius: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "flex-start",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              color: "#dc2626",
              fontSize: 12,
              fontWeight: 900,
              letterSpacing:
                "0.08em",
              textTransform:
                "uppercase",
            }}
          >
            OWNER DECISION
          </div>

          <h3
            className="ns-card-title"
            style={{
              margin:
                "5px 0 6px",
            }}
          >
            Review and Decide
          </h3>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            {isFR
              ? "Examinez les propositions humaines et décidez explicitement de ce qui entre dans le scénario."
              : "Review the human proposals and explicitly decide what enters the screenplay."}
          </p>
        </div>

        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            loading ||
            Boolean(
              workingId
            )
          }
          onClick={
            loadReview
          }
        >
          {loading
            ? (
                isFR
                  ? "Chargement…"
                  : "Loading…"
              )
            : (
                isFR
                  ? "Actualiser"
                  : "Refresh"
              )}
        </button>
      </div>

      {error ? (
        <div
          role="alert"
          style={{
            marginTop: 14,
            padding: 12,
            borderRadius: 12,
            background: "#fef2f2",
            color: "#991b1b",
            fontWeight: 700,
          }}
        >
          {error}
        </div>
      ) : null}

      {notice ? (
        <div
          role="status"
          style={{
            marginTop: 14,
            padding: 12,
            borderRadius: 12,
            background: "#f0fdf4",
            color: "#166534",
            fontWeight: 700,
          }}
        >
          {notice}
        </div>
      ) : null}

      {!loading &&
      !proposalRound ? (
        <div
          style={{
            marginTop: 18,
          }}
        >
          <p
            className="ns-card-meta"
          >
            {isFR
              ? "Aucun Proposal Round ouvert n’attend une décision."
              : "No open Proposal Round is waiting for a decision."}
          </p>

          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={() =>
              onOpenProposal?.()
            }
          >
            {isFR
              ? "Ouvrir Proposal Round"
              : "Open Proposal Round"}
          </button>
        </div>
      ) : proposalRound ? (
        <div
          style={{
            marginTop: 18,
            display: "grid",
            gap: 14,
          }}
        >
          <div
            style={{
              padding: 14,
              borderRadius: 14,
              background: "#fff7f7",
              border:
                "1px solid rgba(220,38,38,0.18)",
            }}
          >
            <strong>
              {
                proposalRound
                  .openingReason
              }
            </strong>

            <div
              className="ns-card-meta"
              style={{
                marginTop: 5,
              }}
            >
              {isFR
                ? `${pending.length} contribution(s) en attente`
                : `${pending.length} pending contribution(s)`}
            </div>
          </div>

          {pending.length ===
          0 ? (
            <div>
              <p
                className="ns-card-meta"
              >
                {isFR
                  ? "Il n’y a plus de contribution en attente. Revenez au Proposal Round pour le clôturer."
                  : "There are no pending contributions. Return to Proposal Round to close it."}
              </p>

              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={() =>
                  onOpenProposal?.()
                }
              >
                Proposal Round
              </button>
            </div>
          ) : (
            <>
              {pending.map(
                (
                  contribution,
                  index
                ) => {
                  const id =
                    clean(
                      contribution
                        ?.id
                    );

                  const working =
                    workingId ===
                    id;

                  return (
                    <article
                      key={id}
                      style={{
                        padding: 16,
                        border:
                          "1px solid rgba(148,163,184,0.28)",
                        borderRadius:
                          14,
                        background:
                          "#ffffff",
                      }}
                    >
                      <div
                        style={{
                          display:
                            "flex",
                          justifyContent:
                            "space-between",
                          gap: 10,
                          flexWrap:
                            "wrap",
                        }}
                      >
                        <div>
                          <strong>
                            {index + 1}.{" "}
                            {humanType(
                              contribution
                                ?.contributionType
                            )}
                          </strong>

                          <div
                            className="ns-card-meta"
                            style={{
                              marginTop: 4,
                            }}
                          >
                            {isFR
                              ? "Proposé par"
                              : "Submitted by"}{" "}
                            <strong>
                              {clean(
                                contribution
                                  ?.contributor
                                  ?.displayName
                              ) ||
                                (
                                  isFR
                                    ? "Collaborateur"
                                    : "Collaborator"
                                )}
                            </strong>
                          </div>
                        </div>

                        <span
                          className="ns-card-meta"
                        >
                          {targetLabel(
                            contribution
                          )}
                        </span>
                      </div>

                      <div
                        style={{
                          marginTop: 12,
                          padding: 13,
                          borderRadius:
                            12,
                          background:
                            "#f8fafc",
                        }}
                      >
                        {proposalBody(
                          contribution
                        )}
                      </div>

                      <label
                        style={{
                          display:
                            "block",
                          marginTop: 13,
                        }}
                      >
                        <strong>
                          {isFR
                            ? "Note de décision"
                            : "Decision note"}
                        </strong>

                        <textarea
                          className="ns-input"
                          rows={3}
                          maxLength={1200}
                          value={
                            notes[id] ||
                            ""
                          }
                          disabled={
                            Boolean(
                              workingId
                            )
                          }
                          onChange={(
                            event
                          ) =>
                            setDecisionNote(
                              id,
                              event
                                .target
                                .value
                            )
                          }
                          style={{
                            marginTop: 7,
                            width:
                              "100%",
                          }}
                        />
                      </label>

                      <div
                        style={{
                          display:
                            "flex",
                          gap: 10,
                          flexWrap:
                            "wrap",
                          marginTop:
                            12,
                        }}
                      >
                        <button
                          className="ns-button ns-button-primary"
                          type="button"
                          disabled={
                            Boolean(
                              workingId
                            ) ||
                            !clean(
                              notes[id]
                            )
                          }
                          onClick={() =>
                            decide(
                              contribution,
                              "accept"
                            )
                          }
                        >
                          {working
                            ? (
                                isFR
                                  ? "Traitement…"
                                  : "Working…"
                              )
                            : (
                                isFR
                                  ? "Accepter"
                                  : "Accept"
                              )}
                        </button>

                        <button
                          className="ns-button ns-button-outline"
                          type="button"
                          disabled={
                            Boolean(
                              workingId
                            ) ||
                            !clean(
                              notes[id]
                            )
                          }
                          onClick={() =>
                            decide(
                              contribution,
                              "reject"
                            )
                          }
                        >
                          {isFR
                            ? "Rejeter"
                            : "Reject"}
                        </button>
                      </div>
                    </article>
                  );
                }
              )}

              <form
                onSubmit={
                  rejectAll
                }
                style={{
                  paddingTop: 6,
                }}
              >
                <label>
                  <strong>
                    {isFR
                      ? "Rejeter toutes les contributions en attente"
                      : "Reject all pending contributions"}
                  </strong>

                  <textarea
                    className="ns-input"
                    rows={3}
                    maxLength={1200}
                    value={
                      rejectAllNote
                    }
                    disabled={
                      Boolean(
                        workingId
                      )
                    }
                    onChange={(
                      event
                    ) =>
                      setRejectAllNote(
                        event.target
                          .value
                      )
                    }
                    style={{
                      display: "block",
                      marginTop: 7,
                      width: "100%",
                    }}
                  />
                </label>

                <button
                  className="ns-button ns-button-outline"
                  type="submit"
                  disabled={
                    Boolean(
                      workingId
                    ) ||
                    !clean(
                      rejectAllNote
                    )
                  }
                  style={{
                    marginTop: 10,
                  }}
                >
                  {isFR
                    ? "Rejeter tout et clôturer"
                    : "Reject All and Close Round"}
                </button>
              </form>
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}
